import {
    DossierOpenSearch,
    NestedDossierFormaliteOpenSearch,
    NestedDossierProcessesOpenSearch,
    useFormaliteService,
} from '@europrocurement/l2d-domain';
import { PaletteColor, useTheme } from '@mui/material';
import { useDossierValidatorsCtx, useFormalitiesCtx } from '@europrocurement/l2d-modules/providers';
import { useStatusStyle } from '@europrocurement/l2d-modules/hooks';
import { USER_DEFAULT_FORMALISTE } from '@europrocurement/l2d-modules/modules/Utilisateur';
import { useTiersCtx } from '@europrocurement/l2d-modules/providers/TiersProvider';
import {
    PROCESS_STATUS_FLAG_DONE,
    PROCESS_STATUS_FLAG_ERROR,
    PROCESS_STATUS_FLAG_FAILED,
    PROCESS_STATUS_FLAG_ONGOING,
    PROCESS_STATUS_FLAG_PENDING,
    VALIDATOR_DEMAT_FACILE,
    VALIDATOR_INPI,
    VALIDATOR_TYPE_GREFFE,
} from '../constants/validators';
import { STEP_PREPARATION } from '../constants/steps';

export type FormalitiesStatus = 'pending' | 'ready' | 'ongoing' | 'done' | 'failed';
export type ArticleValidationProcessStatus =
    | 'to-link'
    | 'pending'
    | 'ongoing'
    | 'done'
    | 'failed'
    | 'rescheduled'
    | 'validate'
    | 'needAction'
    | 'default';
export type ArticleBodaccValidationProcessStatus =
    | 'to-link'
    | '0'
    | '1'
    | '2'
    | '3'
    | '4'
    | '5'
    | '6';

const useDossierProcesses = () => {
    const { palette } = useTheme();
    const { validationProcessApi, dossierApi } = useFormaliteService();
    const { getDossierValidators } = useDossierValidatorsCtx();
    const { getValidatorStatusStyle } = useStatusStyle();
    const { getFormalityById } = useFormalitiesCtx();
    const { getTiersById } = useTiersCtx();

    const sortById = (a: NestedDossierProcessesOpenSearch, b: NestedDossierProcessesOpenSearch) => {
        if (a.id > b.id) {
            return -1;
        }

        if (a.id < b.id) {
            return 1;
        }

        return 0;
    };

    const canCreateValidatorProcess = (
        dossier: DossierOpenSearch,
        validatorTypeId: number,
    ): boolean => {
        const statusId = dossier.statutId as unknown as string;
        const filteredValidators = getDossierValidators().filter((v) => {
            const typeId = v.typeId as unknown as number;
            const validatorStatuses = v.dossierStatuses || [];

            return (
                typeId === validatorTypeId &&
                (validatorStatuses.length === 0 ||
                    validatorStatuses?.includes(statusId as unknown as number))
            );
        });

        return filteredValidators.length > 0;
    };

    const processHasFormality = (
        validationProcess: NestedDossierProcessesOpenSearch,
        formality: NestedDossierFormaliteOpenSearch,
    ): boolean => {
        const result = validationProcess.articles.find((articleId) =>
            (formality.articleIds || []).includes(articleId),
        );

        return result !== undefined;
    };

    const deleteFormality = async (formalityId: number, dossier: DossierOpenSearch) => {
        await dossierApi.apiDossiersIdFormalityFormalityIdDelete({
            id: dossier.id as number,
            formalityId,
        });
    };

    const canDeleteFormality = (dossier: DossierOpenSearch): boolean => {
        if (dossier.invoiced) {
            return false;
        }

        if (dossier.formalites.length === 1 && !dossier.transitions?.includes('cancel')) {
            return false;
        }

        const notPendingProcesses = dossier.validationProcesses.filter(
            (p) => p.statusFlag !== PROCESS_STATUS_FLAG_PENDING,
        );

        if (dossier.validationProcesses.length > 0 && notPendingProcesses.length > 0) {
            return false;
        }

        return true;
    };

    const getValidatorProcesses = (
        dossier: DossierOpenSearch,
        validatorTypeId: number,
    ): NestedDossierProcessesOpenSearch[] =>
        dossier?.validationProcesses
            .filter((process) => process.validatorTypeId === validatorTypeId)
            .sort(sortById);

    const getMultipleValidatorProcesses = (
        dossier: DossierOpenSearch,
        validatorTypeIds: number[],
    ): NestedDossierProcessesOpenSearch[] =>
        dossier.validationProcesses
            .filter((process) => validatorTypeIds.includes(process.validatorTypeId))
            .sort(sortById);

    const getExternalUrlByFormality = (
        dossier: DossierOpenSearch,
        formality: NestedDossierFormaliteOpenSearch,
    ): string | undefined => {
        const onGoingOrValidProcess = dossier.validationProcesses
            .filter(
                (validationProcess) =>
                    processHasFormality(validationProcess, formality) &&
                    [PROCESS_STATUS_FLAG_ONGOING, PROCESS_STATUS_FLAG_DONE].includes(
                        validationProcess.statusFlag,
                    ) &&
                    validationProcess.validatorTypeId === VALIDATOR_TYPE_GREFFE,
            )
            .sort(sortById)
            .find((validationProcess) => processHasFormality(validationProcess, formality));

        if (onGoingOrValidProcess) {
            return onGoingOrValidProcess.externalUrl;
        }

        const errorProcess = dossier.validationProcesses
            .filter(
                (validationProcess) =>
                    processHasFormality(validationProcess, formality) &&
                    validationProcess.statusFlag === PROCESS_STATUS_FLAG_ERROR &&
                    validationProcess.validatorTypeId === VALIDATOR_TYPE_GREFFE,
            )
            .sort(sortById)
            .find((validationProcess) => processHasFormality(validationProcess, formality));

        return errorProcess?.externalUrl;
    };

    const getCurrentProcessOfType = (dossier: DossierOpenSearch, type: number) => {
        const processes = getValidatorProcesses(dossier, type);

        return processes
            .sort((a, b) => {
                const aCreatedAt = a.createdAt ? new Date(a.createdAt) : new Date();
                const bCreatedAt = b.createdAt ? new Date(b.createdAt) : new Date();

                return aCreatedAt.getTime() > bCreatedAt.getTime() ? -1 : 1;
            })
            .find(() => true);
    };

    const canChangeProcessStatus = (process: NestedDossierProcessesOpenSearch): boolean =>
        ![PROCESS_STATUS_FLAG_DONE].includes(process.statusFlag);

    const getFormalitiesStatus = (dossier: DossierOpenSearch): string | undefined => {
        const currentProcess = getCurrentProcessOfType(dossier, VALIDATOR_TYPE_GREFFE);

        if (!currentProcess) {
            return undefined;
        }

        return currentProcess.statusCode;
    };

    const isFormalityComplete = (
        dossier: DossierOpenSearch,
        formality: NestedDossierFormaliteOpenSearch,
    ): boolean => {
        const process = dossier.validationProcesses.find(
            (validationProcess) =>
                processHasFormality(validationProcess, formality) &&
                validationProcess.statusFlag === PROCESS_STATUS_FLAG_DONE &&
                validationProcess.validatorTypeId === VALIDATOR_TYPE_GREFFE,
        );

        return process !== undefined;
    };

    const createValidatorProcess = async (
        dossierId: number,
        articleIds?: Array<number>,
        identifier?: string,
        externalUrl?: string,
        validatorId?: number,
    ) =>
        validationProcessApi.apiDossiersValidationPost({
            procDureDeValidationDesDossiersCreateDossierValidationProcessDtoJsonldValidatorsProcessCreate:
                {
                    dossier: dossierId as unknown as string,
                    articles: (articleIds || []) as unknown[] as string[],
                    identifier,
                    externalUrl,
                    validator: validatorId as unknown as string,
                },
        });

    const updateValidatorProcess = async (
        validationProcessId: number,
        transition: string,
        identifier?: string,
        externalUrl?: string,
        deleteDetail?: boolean,
    ) =>
        await validationProcessApi.apiDossiersValidationIdActionPut({
            id: validationProcessId,
            action: transition,
            apiDossiersValidationIdActionPutRequest: {
                identifier,
                externalUrl,
                deleteDetail,
            },
        });

    const publishGreffeValidatorProcess = async (dossier: DossierOpenSearch) => {
        const currentProcess = getCurrentProcessOfType(dossier, VALIDATOR_TYPE_GREFFE);

        if (!currentProcess) {
            Promise.reject();
        }

        return updateValidatorProcess(currentProcess?.id as number, 'df_created');
    };

    const getFormalitiesByProcess = (
        dossier: DossierOpenSearch,
        processes: Array<NestedDossierProcessesOpenSearch>,
    ): Array<NestedDossierFormaliteOpenSearch> => {
        const processesArticleIds = processes.map((process) => process.articles).flat();

        return dossier.formalites.filter((formality) => {
            const formalityArticleId = formality.articleIds.find((articleId) =>
                processesArticleIds.includes(articleId),
            );

            return formalityArticleId !== undefined;
        });
    };

    const canPublish = (dossier: DossierOpenSearch) => {
        // Check if there is a formalist for this dossier
        if (!dossier?.formalisteId || dossier.formalisteId === USER_DEFAULT_FORMALISTE) {
            return false;
        }

        // Check if there is only Demat Facile processes
        const processes = getValidatorProcesses(dossier, VALIDATOR_TYPE_GREFFE);
        const notDfProcesses = processes.find(
            (process) =>
                process.validatorId !== VALIDATOR_DEMAT_FACILE &&
                (process.statusFlag === PROCESS_STATUS_FLAG_ERROR ||
                    process.statusFlag === PROCESS_STATUS_FLAG_ONGOING ||
                    process.statusFlag === PROCESS_STATUS_FLAG_PENDING),
        );

        if (processes.length > 0 && notDfProcesses) {
            return false;
        }

        // Check if client opere SIREN is required, depending of formality type
        const clientOpere = dossier?.clientOpereId
            ? getTiersById(dossier?.clientOpereId)
            : undefined;

        if (!clientOpere?.siren) {
            const formalities = getFormalitiesByProcess(dossier, processes);
            const allowNoSiren = formalities.reduce((accumulator, current) => {
                const formality = getFormalityById(current.id);

                if (!formality) {
                    return false;
                }

                return !formality?.sirenRequired && accumulator;
            }, true);

            if (!allowNoSiren) {
                return false;
            }
        }

        // Check main status for greffe processes
        const status = getFormalitiesStatus(dossier);

        return status === 'pending' || status === 'ready' || status === 'failed';
    };

    const getNotPublishableReason = (dossier: DossierOpenSearch) => {
        const reasons = [];
        if (!dossier?.formalisteId || dossier.formalisteId === USER_DEFAULT_FORMALISTE) {
            reasons.push('formaliste non renseigné');
        }

        const clientOpere = dossier?.clientOpereId
            ? getTiersById(dossier?.clientOpereId)
            : undefined;

        if (!clientOpere?.siren) {
            const processes = getValidatorProcesses(dossier, VALIDATOR_TYPE_GREFFE);
            const formalities = getFormalitiesByProcess(dossier, processes);
            const allowNoSiren = formalities.reduce((accumulator, current) => {
                const formality = getFormalityById(current.id);

                if (!formality) {
                    return false;
                }

                return !formality?.sirenRequired && accumulator;
            }, true);

            if (!allowNoSiren) {
                reasons.push('client opéré sans SIREN');
            }
        }

        if (!reasons.length) {
            return undefined;
        }

        return `Le dossier ne peut pas être déposé pour les raisons suivantes: ${reasons.join(', ')}`;
    };

    const getFormalitiesStatusLabel = (dossier: DossierOpenSearch): string => {
        const currentProcess = getCurrentProcessOfType(dossier, VALIDATOR_TYPE_GREFFE);

        return currentProcess ? currentProcess.statusLabel : '';
    };

    const getFormalitiesStatusColor = (dossier: DossierOpenSearch): PaletteColor => {
        const currentProcess = getCurrentProcessOfType(dossier, VALIDATOR_TYPE_GREFFE);

        if (currentProcess?.statusFlag) {
            const { colorPalette } = getValidatorStatusStyle(currentProcess.statusFlag);

            return colorPalette;
        }

        return palette.primary;
    };

    const canAddFormality = (
        dossier: DossierOpenSearch,
        siblings: DossierOpenSearch[] = [],
    ): boolean => {
        if (!dossier?.stepId) {
            return false;
        }

        // Prevent add formality if already invoiced
        const hasInvoicedSibling = siblings.some((doss) => doss?.invoiced);
        if (hasInvoicedSibling) {
            return false;
        }

        return dossier.stepId <= STEP_PREPARATION;
    };

    const getProcessesByArticle = (
        dossier: DossierOpenSearch,
        validatorTypeId: number,
        articleId: number,
    ): Array<NestedDossierProcessesOpenSearch> =>
        getValidatorProcesses(dossier, validatorTypeId)
            .filter((process) => process.articles.includes(articleId))
            .sort(sortById);

    const findProcessByArticle = (
        dossier: DossierOpenSearch,
        validatorTypeIds: number[],
        articleId: number,
    ): NestedDossierProcessesOpenSearch | undefined =>
        getMultipleValidatorProcesses(dossier, validatorTypeIds).find((process) =>
            process.articles.includes(articleId),
        );

    const getProcessesByArticles = (
        dossier: DossierOpenSearch,
        validatorTypeId: number,
        articleIds: number[],
    ): Array<NestedDossierProcessesOpenSearch> =>
        getValidatorProcesses(dossier, validatorTypeId)
            .filter((process) =>
                process.articles.some((articleId) => articleIds.includes(articleId)),
            )
            .sort(sortById);

    const filterProcessesByStatusFlag = (
        processes: Array<NestedDossierProcessesOpenSearch>,
        flag: number,
    ) => processes.filter((process) => flag === process.statusFlag);

    const hasOnGoingProcesses = (processes: Array<NestedDossierProcessesOpenSearch>) => {
        const onGoingProcesses = filterProcessesByStatusFlag(
            processes,
            PROCESS_STATUS_FLAG_ONGOING,
        );

        if (onGoingProcesses.length > 0) {
            return true;
        }

        return filterProcessesByStatusFlag(processes, PROCESS_STATUS_FLAG_ERROR).length > 0;
    };

    const getProcessStatusByArticle = (
        dossier: DossierOpenSearch,
        validatorTypeId: number,
        articleId: number,
    ): ArticleValidationProcessStatus => {
        const processes = getProcessesByArticle(dossier, validatorTypeId, articleId);

        if (processes.length) {
            const onGoingProcesses = filterProcessesByStatusFlag(
                processes,
                PROCESS_STATUS_FLAG_ONGOING,
            );
            const validProcesses = filterProcessesByStatusFlag(processes, PROCESS_STATUS_FLAG_DONE);
            const errorProcesses = filterProcessesByStatusFlag(
                processes,
                PROCESS_STATUS_FLAG_ERROR,
            );
            const failedProcesses = filterProcessesByStatusFlag(
                processes,
                PROCESS_STATUS_FLAG_FAILED,
            );

            if (onGoingProcesses.length) {
                return 'ongoing';
            }

            if (validProcesses.length) {
                return 'done';
            }

            if (errorProcesses.length) {
                return 'needAction';
            }

            if (failedProcesses.length) {
                return 'failed';
            }
        }

        return 'pending';
    };

    const getArticleProcessesStatus = (
        dossier: DossierOpenSearch,
        validatorTypeId: number,
        articleIds: Array<number>,
    ): ArticleValidationProcessStatus => {
        const statuses = articleIds.map((articleId) =>
            getProcessStatusByArticle(dossier, validatorTypeId, articleId),
        );

        const valid = statuses.filter((status) => status === 'done');
        const pending = statuses.filter((status) => status === 'pending');
        const needAction = statuses.filter((status) => status === 'needAction');
        const error = statuses.filter((status) => status === 'failed');

        if (pending.length === statuses.length) {
            return 'pending';
        }
        if (needAction.length === statuses.length) {
            return 'needAction';
        }
        if (valid.length === statuses.length) {
            return 'done';
        }
        if (error.length === statuses.length) {
            return 'failed';
        }

        return 'ongoing';
    };

    const getArticleLastValidProcess = (
        dossier: DossierOpenSearch,
        validatorTypeId: number,
        articleId: number,
    ): NestedDossierProcessesOpenSearch | undefined => {
        const processes = getProcessesByArticle(dossier, validatorTypeId, articleId);

        return processes
            .filter((item) => item.statusCode !== 'failed')
            .sort(sortById)
            .find(() => true);
    };

    const getArticleProcessesStatusLabel = (status: ArticleValidationProcessStatus) => {
        const labels = new Map([
            ['pending', 'À faire'],
            ['ongoing', 'En cours'],
            ['needAction', 'En cours'],
            ['done', 'Finalisé'],
            ['failed', 'Échoué'],
        ]);

        return labels.get(status) || 'Inconnu';
    };

    const getArticleProcessesStatusColor = (status: ArticleValidationProcessStatus) => {
        const colors = new Map([
            ['pending', palette.amber as unknown as PaletteColor],
            ['ongoing', palette.blue as unknown as PaletteColor],
            ['done', palette.green as unknown as PaletteColor],
            ['needAction', palette.deepOrange as unknown as PaletteColor],
            ['failed', palette.red as unknown as PaletteColor],
        ]);

        return colors.get(status) || palette.primary;
    };

    const canToggleGreffeValidator = (dossier: DossierOpenSearch) => {
        const validators = getFormalitiesByProcess(dossier, dossier?.validationProcesses || [])
            .map((f) => getFormalityById(f.id)?.validators)
            .flat()
            .map((v) => v?.validator);

        return validators.includes(VALIDATOR_DEMAT_FACILE) && validators.includes(VALIDATOR_INPI);
    };

    const toggleGreffeValidator = async (
        dossier: DossierOpenSearch,
        process: NestedDossierProcessesOpenSearch,
    ) => {
        if (canChangeProcessStatus(process)) {
            await updateValidatorProcess(process?.id as number, 'cancelled');
        }

        const newValidatorId =
            process.validatorId === VALIDATOR_DEMAT_FACILE
                ? VALIDATOR_INPI
                : VALIDATOR_DEMAT_FACILE;

        await createValidatorProcess(
            dossier.id as number,
            process.articles,
            undefined,
            undefined,
            newValidatorId,
        );
    };

    const isProcessOfValidator = (
        process: NestedDossierProcessesOpenSearch,
        validatorId: number,
    ): boolean => process.validatorId === validatorId;

    const canUpdateIdentifier = (process: NestedDossierProcessesOpenSearch): boolean =>
        process.statusFlag === PROCESS_STATUS_FLAG_PENDING &&
        isProcessOfValidator(process, VALIDATOR_INPI);

    return {
        isFormalityComplete,
        publishGreffeValidatorProcess,
        getFormalitiesStatusLabel,
        getFormalitiesStatusColor,
        canAddFormality,
        canPublish,
        getNotPublishableReason,
        getExternalUrlByFormality,
        canCreateValidatorProcess,
        createValidatorProcess,
        updateValidatorProcess,
        getProcessStatusByArticle,
        findProcessByArticle,
        getProcessesByArticle,
        getProcessesByArticles,
        getArticleProcessesStatus,
        getArticleProcessesStatusLabel,
        getArticleProcessesStatusColor,
        getArticleLastValidProcess,
        hasOnGoingProcesses,
        getCurrentProcessOfType,
        canChangeProcessStatus,
        canToggleGreffeValidator,
        toggleGreffeValidator,
        isProcessOfValidator,
        getFormalitiesByProcess,
        deleteFormality,
        canDeleteFormality,
        canUpdateIdentifier,
    };
};

export default useDossierProcesses;
